import { useState, useEffect } from 'react';
import { couriersService } from '@/shared/services/couriersService';
import { Courier } from '@/shared/types/couriers';
import { ConfirmModal } from '@/shared/components/ConfirmModal';
import styles from './CouriersPage.module.css';
import { CourierDetailsModal } from '../components/CourierDetailsModal';

type Tab = 'onboarding' | 'fleet';

export const CouriersPage = () => {
    const [activeTab, setActiveTab] = useState<Tab>('onboarding');
    const [couriers, setCouriers] = useState<Courier[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [processingId, setProcessingId] = useState<string | null>(null);

    // Modal state
    const [modal, setModal] = useState<{
        isOpen: boolean;
        courier: Courier | null;
        action: 'approve' | 'reject' | 'activate' | 'deactivate' | 'block' | 'update_wallet' | 'adjust_balance';
    }>({
        isOpen: false,
        courier: null,
        action: 'approve'
    });
    const [detailsModal, setDetailsModal] = useState<{
        isOpen: boolean;
        courier: Courier | null;
    }>({
        isOpen: false,
        courier: null
    });

    useEffect(() => {
        setLoading(true);
        setError('');

        const unsubscribe = couriersService.subscribe(
            activeTab,
            (newCouriers) => {
                setCouriers(newCouriers);
                setLoading(false);
            },
            (err) => {
                console.error('Error suscribiendo a repartidores:', err);
                setError('Error al conectar con la base de datos de repartidores.');
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, [activeTab]);

    const handleAction = (courier: Courier, action: typeof modal.action) => {
        setModal({ isOpen: true, courier, action });
    };

    const confirmAction = async (inputValue?: string) => {
        if (!modal.courier) return;
        const uid = modal.courier.uid;
        setProcessingId(uid);
        setSuccess('');
        setError('');

        try {
            if (modal.action === 'approve') {
                await couriersService.approve(uid);
                setSuccess('Repartidor aprobado exitosamente.');
            } else if (modal.action === 'reject') {
                await couriersService.reject(uid);
                setSuccess('Solicitud rechazada.');
            } else if (modal.action === 'activate') {
                await couriersService.setStatus(uid, 'activo');
                setSuccess('Repartidor activado.');
            } else if (modal.action === 'deactivate') {
                await couriersService.setStatus(uid, 'inactivo');
                setSuccess('Repartidor desactivado.');
            } else if (modal.action === 'block') {
                await couriersService.setStatus(uid, 'bloqueado');
                setSuccess('Repartidor bloqueado.');
            } else if (modal.action === 'update_wallet') {
                const newLimit = parseInt(inputValue || '0');
                if (isNaN(newLimit)) throw new Error('Monto inválido');
                await couriersService.updateWallet(uid, { creditLimit: newLimit });
                setSuccess('Cupo de crédito actualizado.');
            } else if (modal.action === 'adjust_balance') {
                const newBalance = parseInt(inputValue || '0');
                if (isNaN(newBalance)) throw new Error('Monto inválido');
                await couriersService.updateWallet(uid, {
                    creditLimit: modal.courier.wallet?.creditLimit || 50000,
                    cashBalance: newBalance
                });
                setSuccess('Deuda (Balance) actualizada manualmente.');
            }

            setModal({ ...modal, isOpen: false });
        } catch (err) {
            setError('Error al procesar la acción.');
        } finally {
            setProcessingId(null);
        }
    };

    const getModalContent = () => {
        const name = modal.courier?.fullName;
        switch (modal.action) {
            case 'approve': return { title: '¿Aprobar repartidor?', message: `¿Revisaste los documentos de ${name} y confirmas que son válidos?`, variant: 'primary' as const };
            case 'reject': return { title: '¿Rechazar solicitud?', message: `¿Estás seguro de rechazar la solicitud de ${name}?`, variant: 'danger' as const };
            case 'deactivate': return { title: '¿Desactivar repartidor?', message: `¿Deseas desactivar a ${name}? No podrá recibir pedidos temporalmente.`, variant: 'warning' as const };
            case 'activate': return { title: '¿Activar repartidor?', message: `¿Deseas reactivar a ${name} para que pueda recibir pedidos?`, variant: 'primary' as const };
            case 'block': return { title: '¿Bloquear repartidor?', message: `¿Deseas bloquear definitivamente a ${name}? Esta acción se registra en auditoría.`, variant: 'danger' as const };
            case 'update_wallet': return {
                title: 'Actualizar Cupo de Crédito',
                message: `Define el límite máximo de efectivo que ${name} puede recaudar antes de ser bloqueado.`,
                variant: 'primary' as const,
                showInput: true,
                inputLabel: 'Nuevo Límite de Crédito (COP)',
                inputPlaceholder: 'Ej: 100000',
                inputType: 'number' as const
            };
            case 'adjust_balance': return {
                title: 'Ajustar Deuda (Balance)',
                message: `Define manualmente la deuda actual de ${name}. Usa 0 para limpiar la deuda totalmente.`,
                variant: 'warning' as const,
                showInput: true,
                inputLabel: 'Nueva Deuda Actual (COP)',
                inputPlaceholder: 'Ej: 0',
                inputType: 'number' as const
            };
            default: return { title: '', message: '', variant: 'primary' as const };
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1>Gestión de Repartidores</h1>
                <p>Administra las nuevas solicitudes y la flota activa de repartidores.</p>
            </div>

            <div className={styles.tabs}>
                <button
                    className={`${styles.tab} ${activeTab === 'onboarding' ? styles.active : ''}`}
                    onClick={() => setActiveTab('onboarding')}
                >
                    Solicitudes Nuevas
                </button>
                <button
                    className={`${styles.tab} ${activeTab === 'fleet' ? styles.active : ''}`}
                    onClick={() => setActiveTab('fleet')}
                >
                    Gestión de Flota (Todos)
                </button>
            </div>

            {error && <div className={styles.error}>{error}</div>}
            {success && <div className={styles.success}>{success}</div>}

            <div className={styles.tableContainer}>
                {loading ? (
                    <div className={styles.loader}>Cargando...</div>
                ) : couriers.length === 0 ? (
                    <div className={styles.empty}>No se encontraron repartidores en esta sección.</div>
                ) : (
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Repartidor</th>
                                <th>Vehículo</th>
                                <th>Documento</th>
                                <th>Cupo / Deuda</th>
                                <th>Estado</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {couriers.map((courier) => (
                                <tr key={courier.uid}>
                                    <td>
                                        <div style={{ fontWeight: 600 }}>{courier.fullName}</div>
                                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{courier.email}</div>
                                    </td>
                                    <td>
                                        <div style={{ textTransform: 'capitalize' }}>{courier.vehicle.type}</div>
                                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                            {courier.vehicle.brand} • {courier.vehicle.plate || 'Sin placa'}
                                        </div>
                                    </td>
                                    <td>{courier.validation.documentNumber || 'N/A'}</td>
                                    <td>
                                        <div style={{ fontSize: '0.9rem' }}>
                                            <span style={{ color: (courier.wallet?.cashBalance || 0) >= (courier.wallet?.creditLimit || 50000) ? '#ef4444' : '#10b981', fontWeight: 600 }}>
                                                ${(courier.wallet?.cashBalance || 0).toLocaleString()}
                                            </span>
                                            <span style={{ color: '#6b7280' }}> / ${(courier.wallet?.creditLimit || 50000).toLocaleString()}</span>
                                        </div>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <button
                                                onClick={() => handleAction(courier, 'update_wallet')}
                                                style={{ fontSize: '0.7rem', color: '#3b82f6', border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}
                                            >
                                                Cambiar cupo
                                            </button>
                                            <button
                                                onClick={() => handleAction(courier, 'adjust_balance')}
                                                style={{ fontSize: '0.7rem', color: '#f59e0b', border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}
                                            >
                                                Ajustar deuda
                                            </button>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`${styles.statusBadge} ${styles['status' + courier.status]}`}>
                                            {courier.status}
                                        </span>
                                    </td>
                                    <td>
                                        <div className={styles.actions}>
                                            <button className={`${styles.btn} ${styles.btnDetails}`} onClick={() => setDetailsModal({ isOpen: true, courier })}>Detalles</button>
                                            {courier.status === 'pendiente' && (
                                                <>
                                                    <button className={`${styles.btn} ${styles.btnApprove}`} onClick={() => handleAction(courier, 'approve')}>Aprobar</button>
                                                    <button className={`${styles.btn} ${styles.btnReject}`} onClick={() => handleAction(courier, 'reject')}>Rechazar</button>
                                                </>
                                            )}
                                            {courier.status === 'activo' && (
                                                <>
                                                    <button className={`${styles.btn} ${styles.btnDeactivate}`} onClick={() => handleAction(courier, 'deactivate')}>Desactivar</button>
                                                    <button className={`${styles.btn} ${styles.btnReject}`} onClick={() => handleAction(courier, 'block')}>Bloquear</button>
                                                </>
                                            )}
                                            {(courier.status === 'inactivo' || courier.status === 'bloqueado') && (
                                                <button className={`${styles.btn} ${styles.btnActivate}`} onClick={() => handleAction(courier, 'activate')}>Activar</button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <ConfirmModal
                isOpen={modal.isOpen}
                onClose={() => setModal({ ...modal, isOpen: false })}
                onConfirm={(val) => confirmAction(val)}
                title={getModalContent().title}
                message={getModalContent().message}
                confirmButtonVariant={getModalContent().variant}
                showInput={getModalContent().showInput}
                inputLabel={getModalContent().inputLabel}
                inputPlaceholder={getModalContent().inputPlaceholder}
                inputType={getModalContent().inputType}
                isLoading={!!processingId}
            />

            <CourierDetailsModal
                isOpen={detailsModal.isOpen}
                onClose={() => setDetailsModal({ ...detailsModal, isOpen: false })}
                courier={detailsModal.courier}
            />
        </div>
    );
};
